import * as z from "zod";
import { hsaPayoutSchema } from "./get-hsa-payouts";

/** One order's share of a payout; Gale has already merged split legs and
 * refunds into a single line. */
export const hsaPayoutBreakdownLineSchema = z.object({
  /** null = a constituent Gale could not match to an order — shown, never
   * dropped, so the lines still sum to the payout. */
  orderCode: z.string().nullable(),
  merchantReference: z.string().nullable(),
  customerName: z.string().nullable(),
  /** Net contribution to this payout; a refund-only line is negative. */
  amountCents: z.number().int(),
  /** `capture` | `refund` | … */
  types: z.array(z.string()),
  grossCents: z.number().int().nullable(),
  processingFeeCents: z.number().int().nullable(),
  lmnFeeCents: z.number().int().nullable(),
  totalFeesCents: z.number().int().nullable(),
  /** The Shopify order whose payment session is `merchantReference`; null
   * when Shopify has no match or could not be asked. */
  shopifyOrderName: z.string().nullable(),
  /** Refunded in this payout. The fields below are null until Gale reports
   * them. */
  refundedCents: z.number().int().nullable(),
  /** Fees returned to the merchant on that refund. */
  feeReversedCents: z.number().int().nullable(),
  /** Order fees the merchant keeps after the refund; null without one. */
  feeKeptCents: z.number().int().nullable(),
  /** The order's captured amount on HSA/FSA cards and on other cards. */
  hsaFsaCapturedCents: z.number().int().nullable(),
  otherCapturedCents: z.number().int().nullable(),
  /** `issued` | `reused` | `none` from Gale's LMN survey record. */
  lmnLetter: z.string().nullable(),
});
export type HsaPayoutBreakdownLine = z.infer<
  typeof hsaPayoutBreakdownLineSchema
>;

/** One payout and its lines. A payout the team doesn't own is not-found. */
export const getHsaPayout = {
  name: "getHsaPayout" as const,
  input: z.object({ payoutId: z.string().min(1) }),
  output: z.object({
    payout: hsaPayoutSchema,
    lines: z.array(hsaPayoutBreakdownLineSchema),
    transactionCount: z.number().int(),
  }),
};
